import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { NavItem } from './models/nav-item';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { DefaultNavItem } from './models/default-nav-item';
import { UniversalVariantsNavSubItem } from './nav-sub-item/models/universal-variants-sub-nav-item';
import { DefaultNavSubItem } from './nav-sub-item/models/default-nav-sub-item';
import { BaseViewModel } from '../../../../models/base/base-view-model';
import { UserDomainModel } from '../../../../domainModels/user-domain-model';
import { DistinctUtils } from '../../../../utils/distinct-utils';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { HydratedAdminUser } from '../../../../models/account/dto/hydrated-admin-user';
import { NavItemId } from 'src/app/models/enum/shared/nav-item-id.enum';
import { WebSocketService } from '../../../../services/web-socket.service';
import { ConnectionStatus } from '../../../../models/web-sockets/enums/connection-status';
import { ProductGroupsNavSubItem } from './nav-sub-item/models/product-groups-sub-nav-item';
import { SignOutNavItem } from './models/sign-out-nav-item';
import { ProductsNavItem } from './models/products-nav-item';
import { DataSyncingService } from '../../../../services/data-sync/data-syncing.service';

@Injectable()
export class SidebarNavViewModel extends BaseViewModel {
  constructor(
    private userDomainModel: UserDomainModel,
    private router: Router,
    private webSocketService: WebSocketService,
    private dataSyncingService: DataSyncingService
  ) {
    super();
    this.bindUrlParams();
  }

  protected override _loadingOpts = new BehaviorSubject(this.getLoadingOptions());
  private readonly user$ = this.userDomainModel.user$;

  public readonly webSocketConnectionColor$ = this.webSocketService.socketConnectionStatus$.pipe(
    map(connectionStatus => {
      switch (connectionStatus) {
        case ConnectionStatus.Connected:
          return '#63d58f';
        case ConnectionStatus.Connecting:
          return '#ffd74b';
        case ConnectionStatus.Disconnected:
          return '#fa5555';
      }
    })
  );

  // Mirrors budsense-client's side-nav footer: the refresh spinner replaces the connection dot only while connected.
  public readonly showSyncingIndicator$ = combineLatest([
    this.webSocketService.socketConnectionStatus$,
    this.dataSyncingService.syncing$
  ]).pipe(
    map(([connectionStatus, syncing]) => connectionStatus === ConnectionStatus.Connected && syncing),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected _currentNavUrl = new BehaviorSubject<string>('');
  public currentNavUrl$ = this._currentNavUrl.pipe(distinctUntilChanged());

  // @ts-ignore
  public readonly versionString$ = of(require('package.json')?.version);
  public readonly navItems$: Observable<NavItem[]> = this.user$.pipe(
    map(user => this.getNavItems(user)),
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiableArray)
  );

  private bindUrlParams() {
    this._currentNavUrl.next(this.router.url);
    this.router.events.pipe(filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd)).subscribeWhileAlive({
      owner: this,
      next: evt => this.urlChanged(evt)
    });
  }

  private getLoadingOptions(): LoadingOptions {
    const loadingOpts = LoadingOptions.default();
    loadingOpts.fullscreen = true;
    loadingOpts.zIndex = 100;
    return loadingOpts;
  }

  private getNavItems(user: HydratedAdminUser): NavItem[] {
    const navItems = [];
    const dashboard = this.getDashboardNavItem();
    const create = this.getCreateNavItem();
    const customers = this.getCustomersNavItem();
    const products = this.getProductsNavItem();
    const themes = this.getThemesNavItem();
    const defaults = this.getDefaultsNavItem();
    const budBot = this.getBudBotNavItem();
    const settings = this.getSettingsNavItem();
    const profile = this.getProfileNavItem();
    const signOut = this.getSignOutNavItem();

    if (user?.productOnlyAccess) {
      navItems.push(products, profile, signOut);
    } else {
      navItems.push(dashboard, create, customers, products, themes, defaults, budBot, settings, profile, signOut);
    }
    return navItems;
  }

  private getDashboardNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/home.svg';
    const filledIcon = 'assets/icons/dark/solid/home.svg';
    return new DefaultNavItem(NavItemId.Dashboard, 'Dashboard', [], '/metrics', nonFilledIcon, filledIcon);
  }

  private getCreateNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/plus-circle.svg';
    const filledIcon = 'assets/icons/dark/solid/plus-circle.svg';
    return new DefaultNavItem(NavItemId.Create, 'Create', [], '/create', nonFilledIcon, filledIcon);
  }

  private getCustomersNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/users.svg';
    const filledIcon = 'assets/icons/dark/solid/users.svg';
    return new DefaultNavItem(NavItemId.Customers, 'Customers', [], '/customers', nonFilledIcon, filledIcon);
  }

  private getProductsNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/view-grid.svg';
    const filledIcon = 'assets/icons/dark/solid/view-grid.svg';
    const universalVariants = new UniversalVariantsNavSubItem('Universal Variants', 'universalvariants');
    const productGroups = new ProductGroupsNavSubItem('Product Groups', 'productgroups');
    const brands = new DefaultNavSubItem('Brands', 'brands');
    const variantLookup = new DefaultNavSubItem('Variant Lookup', 'variantlookup');
    const productSubItems = [universalVariants, productGroups, brands, variantLookup];
    return new ProductsNavItem(NavItemId.Products, 'Products', productSubItems, '/products', nonFilledIcon, filledIcon);
  }

  private getThemesNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/template.svg';
    const filledIcon = 'assets/icons/dark/solid/template.svg';
    return new DefaultNavItem(NavItemId.Themes, 'Themes', [], '/themes', nonFilledIcon, filledIcon);
  }

  private getDefaultsNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/lock-closed.svg';
    const filledIcon = 'assets/icons/dark/solid/lock-closed.svg';
    const sections = new DefaultNavSubItem('Sections', 'sections');
    const smartFilters = new DefaultNavSubItem('Smart Filters', 'smartfilters');
    const highGuideFilters = new DefaultNavSubItem('HighGuide Filters', 'highGuideFilters');
    const customization = new DefaultNavSubItem('Customization', 'customization');
    const productTags = new DefaultNavSubItem('Product Tags', 'productTags');
    const productSubItems = [sections, smartFilters, highGuideFilters, customization, productTags];
    return new DefaultNavItem(NavItemId.Defaults, 'Defaults', productSubItems, '/defaults', nonFilledIcon, filledIcon);
  }

  private getBudBotNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/BudBot.svg';
    const filledIcon = 'assets/icons/dark/solid/BudBot.svg';
    return new DefaultNavItem(NavItemId.BudBot, 'BudBot', [], '/budbot', nonFilledIcon, filledIcon);
  }

  private getSettingsNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/cog.svg';
    const filledIcon = 'assets/icons/dark/solid/cog.svg';
    return new DefaultNavItem(NavItemId.Settings, 'Settings', [], '/settings', nonFilledIcon, filledIcon);
  }

  private getProfileNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/user-circle.svg';
    const filledIcon = 'assets/icons/dark/solid/user-circle.svg';
    return new DefaultNavItem(NavItemId.Profile, 'Profile', [], '/account', nonFilledIcon, filledIcon);
  }

  private getSignOutNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/dark/outline/logout.svg';
    const filledIcon = 'assets/icons/dark/solid/logout.svg';
    return new SignOutNavItem(NavItemId.SignOut, 'Sign Out', [], '/auth/sign-out', nonFilledIcon, filledIcon);
  }

  navigateToRoot() {
    this.router.navigate(['']).then(() => {});
  }

  private urlChanged(nav: NavigationEnd) {
    this._currentNavUrl.next(nav.url);
  }
}
